import { Component, DestroyRef, inject, input, output, OnInit, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AgGridAngular } from 'ag-grid-angular';

export type Action = 'create' | 'update' | 'delete' | 'details';
export type SelectedRowAction = 'update' | 'delete' | 'details';

import {
  ColDef,
  GridApi,
  GridReadyEvent,
  SelectionChangedEvent,
  CellClickedEvent,
  CellValueChangedEvent,
  RowClassRules
} from 'ag-grid-community';

import { Observable, Subscription, UnaryFunction, finalize, from, of } from 'rxjs';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { DeleteEventDataModel } from './simple-grid.model';

@Component({
  selector: 'app-simple-grid',
  standalone: true,
  imports: [AgGridAngular],
  templateUrl: './simple-grid.component.html',
  host: { class: 'd-flex flex-fill' }
})
export class SimpleGridComponent implements OnInit, OnDestroy {
  private _modalService = inject(NgbModal);
  private _destroyRef = inject(DestroyRef);

  private refreshSubscription: Subscription | null = null;

  listOperator = input<UnaryFunction<Observable<boolean>, Observable<unknown[]>> | null>(null);
  createOperator = input<UnaryFunction<Observable<unknown>, Observable<void>> | null>(null);
  updateOperator = input<UnaryFunction<Observable<string>, Observable<void>> | null>(null);
  detailsOperator = input<UnaryFunction<Observable<string>, Observable<void>> | null>(null);
  deleteOperator = input<UnaryFunction<Observable<DeleteEventDataModel>, Observable<void>> | null>(null);
  submitOperator = input<UnaryFunction<Observable<unknown>, Observable<void>> | null>(null);
  cellValueChangeOperator = input<UnaryFunction<Observable<CellValueChangedEvent>, Observable<void>> | null>(null);
  editMode = input<'auto' | 'batch'>('auto');
  selectFirstRow = input<boolean>(false);
  enablePagination = input<boolean>(true);
  columnDefs = input<ColDef[]>([]);
  rowActionSelector = input<(row: unknown) => SelectedRowAction[]>(() => ['details']);
  baseActions = input<Action[]>(['create', 'update', 'delete']);
  deselectRowSignal = input<Observable<void> | null>(null);
  rowClassRules = input<RowClassRules<unknown>>({});
  selected = output<string | null>();
  clicked = output<CellClickedEvent>();

  created = output();
  updated = output();
  deleted = output();
  submitted = output();

  removing = false;
  cloning = false;
  submitting = false;
  rowSelected = false;

  rowSelection: 'single' | 'multiple' | undefined = 'single';
  defaultColDef = {
    sortable: true,
    filter: true,
    floatingFilter: true,
    flex: 1,
    minWidth: 125
    // cellRendererSelector: (params: ICellRendererParams<unknown>) => {
    //   if (params.colDef?.cellDataType === 'boolean') {
    //     return { component: BooleanRendererComponent };
    //   }
    //   return undefined;
    // }
  };
  gridApi: GridApi | null = null;
  selectedRowActions: SelectedRowAction[] = [];

  ngOnInit() {
    const deselectRowSignal = this.deselectRowSignal();
    if (!deselectRowSignal) return;
    this.refreshSubscription = deselectRowSignal.subscribe(() => {
      this.gridApi?.deselectAll();
    });
  }

  gridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.refresh();
  }

  rowDoubleClicked() {
    if (!this.updateOperator()) {
      this.showDetails();
      return;
    }

    if (this.selectedRowActions.includes('update') || this.baseActions().includes('update')) {
      this.update();
      return;
    }

    this.showDetails();
  }

  selectionChanged(event: SelectionChangedEvent) {
    const selectedRows = event.api.getSelectedRows();
    this.rowSelected = selectedRows.length > 0;
    selectedRows.forEach((row) => this.selected.emit(row.uid));
    this.setSelectedRowActions(selectedRows);
  }

  cellClicked(event: CellClickedEvent) {
    this.clicked.emit(event);
  }

  cellValueChanged(event: CellValueChangedEvent) {
    const cellValueChangeOperator = this.cellValueChangeOperator();
    if (!cellValueChangeOperator) return;

    of(event).pipe(cellValueChangeOperator).subscribe();
  }

  refresh(reload = true) {
    // this.gridApi?.setGridOption('loading', true);
    const listOperator = this.listOperator();
    if (!listOperator) return;

    from([reload])
      .pipe(listOperator, takeUntilDestroyed(this._destroyRef))
      .subscribe((rowData) => {
        this.gridApi?.updateGridOptions({ rowData });
        this.gridApi?.setGridOption('loading', false);
        this.selected.emit(null);
        this.selectFirstGridRow();
      });
  }

  create() {
    const createOperator = this.createOperator();
    if (!createOperator) return;

    of(true)
      .pipe(createOperator, takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.refresh(this.isAutoEditMode());
        this.created.emit();
      });
  }

  update() {
    const updateOperator = this.updateOperator();
    if (!updateOperator) return;
    const selectedRows = this.gridApi?.getSelectedRows();
    if (!selectedRows?.length) return;

    from(selectedRows.map((row) => row.uid))
      .pipe(updateOperator, takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.refresh(this.isAutoEditMode());
        this.updated.emit();
      });
  }

  showDetails() {
    const detailsOperator = this.detailsOperator();
    if (!detailsOperator) return;
    const selectedRows = this.gridApi?.getSelectedRows();
    if (!selectedRows?.length) return;

    from(selectedRows.map((row) => row.uid))
      .pipe(detailsOperator, takeUntilDestroyed(this._destroyRef))
      .subscribe();
  }

  remove() {
    const selectedRows = this.gridApi?.getSelectedRows();
    if (!selectedRows?.length) return;

    const modalRef = this._modalService.open(ConfirmDialogComponent, { centered: true });
    modalRef.componentInstance.message = 'Are you sure you want to continue with the deletion?';

    modalRef.closed.subscribe(() => {
      const deleteOperator = this.deleteOperator();
      if (!deleteOperator) return;
      this.removing = true;

      from(selectedRows.map((row) => ({ uid: row.uid }) as DeleteEventDataModel))
        .pipe(
          deleteOperator,
          takeUntilDestroyed(this._destroyRef),
          finalize(() => (this.removing = false))
        )
        .subscribe(() => {
          this.refresh(this.isAutoEditMode());
          this.deleted.emit();
        });
    });
  }

  submit() {
    const submitOperator = this.submitOperator();
    if (!submitOperator) return;
    this.submitting = true;

    of(true)
      .pipe(
        submitOperator,
        takeUntilDestroyed(this._destroyRef),
        finalize(() => (this.submitting = false))
      )
      .subscribe(() => {
        this.refresh();
        this.submitted.emit();
      });
  }

  ngOnDestroy() {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
  }

  private selectFirstGridRow() {
    if (!this.selectFirstRow()) return;

    const firstRowNode = this.gridApi?.getDisplayedRowAtIndex(0);

    if (firstRowNode) {
      firstRowNode.setSelected(true);
    }
  }

  private isAutoEditMode() {
    return this.editMode() === 'auto';
  }

  private setSelectedRowActions(selectedRows: unknown[]) {
    const rowActionSelector = this.rowActionSelector();
    if (selectedRows.length === 0) {
      this.selectedRowActions = [];
      return;
    }
    if (!rowActionSelector) {
      this.selectedRowActions = ['details'];
      return;
    }
    const actionsForAllSelected = selectedRows.map((row) => rowActionSelector(row));
    this.selectedRowActions = actionsForAllSelected.reduce((acc, arr) => acc.filter((x) => arr.includes(x)));
  }
}
